<!--
  Settings.svelte — the portal Settings view (Svelte 5, runes).

  Owner-console preferences for THIS browser. Currently:
    • Appearance — a 3-way theme picker (Light / Dark / System). Dark mode is
      class-based via the shared @price-check/theme tokens (themeStore toggles
      `.dark` on <html>); this only remembers the choice + resolves "system".
    • Account — the signed-in email (read-only; Sign out lives in the sidebar).

  Add future preferences as more <Card> rows below. Mirrors the desktop's
  SettingsPanel theme picker so the two consoles behave identically.

  Props: { email } — the signed-in account (resolved by App via api.me()).
  IMPORT PATH: views/ → ../lib/ui/Card.svelte · theme store from @price-check/ui
-->
<script lang="ts">
  import { Sun, Moon, Monitor } from "lucide";
  import type { IconNode } from "lucide";
  import {
    Icon, ToggleGroup, ToggleGroupItem,
    themeStore, setTheme, type ThemeMode,
  } from "@price-check/ui";
  import Card from "../lib/ui/Card.svelte";

  let { email }: { email: string } = $props();

  const themeOptions: { value: ThemeMode; label: string; icon: IconNode }[] = [
    { value: "light", label: "Light", icon: Sun },
    { value: "dark", label: "Dark", icon: Moon },
    { value: "system", label: "System", icon: Monitor },
  ];

  // ToggleGroup single-select returns "" when an item is toggled OFF — ignore
  // anything that isn't a real theme so a mode is always selected.
  function onTheme(v: string): void {
    if (v === "light" || v === "dark" || v === "system") setTheme(v);
  }
</script>

<div class="flex max-w-[640px] flex-col gap-5">
  <Card title="Appearance" subtitle="How the owner console looks on this device.">
    <div class="flex flex-wrap items-center justify-between gap-4">
      <div class="min-w-0">
        <div class="text-sm font-semibold text-ink">Theme</div>
        <div class="text-sm text-muted">
          {themeStore.mode === "system"
            ? `Following your system (${themeStore.resolved}).`
            : "Remembered on this browser."}
        </div>
      </div>
      <ToggleGroup type="single" value={themeStore.mode} onValueChange={onTheme} aria-label="Theme">
        {#each themeOptions as opt (opt.value)}
          <ToggleGroupItem value={opt.value}>
            <Icon icon={opt.icon} size={13} />
            {opt.label}
          </ToggleGroupItem>
        {/each}
      </ToggleGroup>
    </div>
  </Card>

  <Card title="Account">
    <div class="flex flex-wrap items-center justify-between gap-4">
      <span class="text-sm font-semibold text-ink">Signed in as</span>
      <span class="text-sm text-muted">{email || "—"}</span>
    </div>
  </Card>
</div>
